import { create } from 'zustand'
import { api, ApiRequestError } from '../lib/api'
import type { LoadState } from '../hooks/useAsyncData'

export type OrchestrationState =
  | 'ready_for_upload'
  | 'ready_for_profile_confirm'
  | 'extraction_wait'
  | 'ready_for_review'
  | 'committed'
  | 'complete'
  | 'abandoned'

export type CapabilityMode = 'managed' | 'byo'

export interface SourceSummary {
  source_id: string
  source_type: 'file' | 'text'
  source_label: string
  char_count: number
  word_count: number
  size_bytes: number | null
}

export interface EducationEntry {
  institution: string
  degree?: string | null
  field?: string | null
  year?: number | null
}

export interface ProfilePrefill {
  full_name: string | null
  email: string | null
  phone: string | null
  linkedin_url: string | null
  location: string | null
  education: EducationEntry[]
  languages: string[]
  confidence: Record<string, 'high' | 'low'>
}

export interface ExtractionSummary {
  status: 'pending' | 'extracting' | 'deduplicating' | 'ready' | 'failed'
  stage: 'text' | 'accomplishments' | 'dedup' | null
  error_reason: string | null
  // Canonical quota details when error_reason === 'quota_exceeded': { spent, cap, source }.
  details: { spent: number | null; cap: number | null; source: string } | null
  sources_total: number
  sources_processed: number
  candidates_found: number
}

export interface ReviewSummary {
  total_proofs: number
  total_roles: number
  unresolved_metadata_questions: number
  optional_content_questions: number
  can_commit_primary: boolean
}

export interface OnboardingV2StatePayload {
  // null for a terminal snapshot returned to an already-completed user (M-B):
  // there is no active session and we deliberately don't mint one.
  session_id: string | null
  orchestration_state: OrchestrationState
  capability_mode: CapabilityMode
  has_valid_api_key: boolean
  sources: SourceSummary[] | null
  profile_prefill: ProfilePrefill | null
  extraction: ExtractionSummary | null
  review: ReviewSummary | null
}

export interface ProfileSavePayload {
  full_name: string
  email: string
  phone?: string | null
  linkedin_url?: string | null
  location?: string | null
  education: EducationEntry[]
  languages: string[]
}

export interface ThinQuestion {
  question_id: string
  question_text: string
  question_number: number
  questions_remaining: number
  candidate_context: string
}

export interface QuestionOption {
  label: string
  value: string
  source_label: string | null
}

export interface ContradictionSource {
  text: string
  label: string
}

export interface ReviewQuestion {
  question_id: string
  type: 'metadata_contradiction' | 'content_contradiction'
  text: string
  field: 'employer' | 'role_title' | 'period' | null
  options: QuestionOption[] | null
  source_a: ContradictionSource | null
  source_b: ContradictionSource | null
  proof_a_id: string | null
  proof_b_id: string | null
  skippable: boolean
}

export interface ReviewProof {
  proof_id: string
  employer: string
  role_title: string
  period: string | null
  canonical_description: string
  competency_tags: string[]
  context_tags: string[]
  authority_notes: string | null
  role_context: string | null
  confidence: 'high' | 'low'
  source_labels: string[]
  discarded: boolean
}

export interface RoleGroup {
  role_key: string
  employer: string
  role_title: string
  period: string | null
  proofs: ReviewProof[]
  questions: ReviewQuestion[]
  has_unresolved_mandatory_questions: boolean
}

export interface ReviewPayload {
  roles: RoleGroup[]
  total_proofs: number
  total_roles: number
  can_commit_primary: boolean
}

export interface ProofEditPatch {
  employer?: string
  role_title?: string
  period?: string
  canonical_description?: string
}

export interface CommitResult {
  proofs_added: number
  roles_created: number
  embeddings_generated: number
  library_url: string
}

interface ApiEnvelope<T> {
  data: T
}

interface OnboardingV2Store {
  state: OnboardingV2StatePayload | null
  sources: SourceSummary[]
  review: ReviewPayload | null
  loadState: LoadState
  error: string | null
  fetchState: () => Promise<void>
  skip: () => Promise<void>

  // Source staging (ready_for_upload)
  stageFile: (file: File) => Promise<void>
  stageText: (text: string) => Promise<void>
  removeSource: (sourceId: string) => Promise<void>
  process: () => Promise<void>

  // Profile (ready_for_profile_confirm)
  saveProfile: (payload: ProfileSavePayload) => Promise<OrchestrationState>

  // Extraction wait → review (extraction_wait)
  continueToReview: () => Promise<void>
  retryExtraction: () => Promise<void>

  // Thin Q&A (extraction_wait)
  fetchNextThinQuestion: () => Promise<ThinQuestion | null>
  answerThinQuestion: (questionId: string, answer: string) => Promise<ThinQuestion | null>
  skipThinQuestion: (questionId: string) => Promise<ThinQuestion | null>

  // Review (ready_for_review)
  fetchReview: () => Promise<void>
  editProof: (proofId: string, patch: ProofEditPatch) => Promise<void>
  discardProof: (proofId: string) => Promise<void>
  restoreProof: (proofId: string) => Promise<void>
  answerQuestion: (questionId: string, value: string, customValue?: string) => Promise<void>
  skipQuestion: (questionId: string) => Promise<void>
  commit: (skipReview?: boolean) => Promise<CommitResult>
}

function messageOf(err: unknown): string {
  if (err instanceof ApiRequestError) return err.error.message
  return err instanceof Error ? err.message : 'An unexpected error occurred'
}

const BASE_PATH = '/api/v1/onboarding/v2'

// Single-flight guard: dedupe rapid duplicate activations of the same mutating
// action (double-clicked Commit, double-fired upload). Concurrent calls with the
// same key share one in-flight promise. A timeout clears the entry so a hung
// request can never permanently deadlock the action.
const _inFlight = new Map<string, Promise<unknown>>()
const SINGLE_FLIGHT_TIMEOUT_MS = 30_000

function singleFlight<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = _inFlight.get(key)
  if (existing) return existing as Promise<T>
  let timer: ReturnType<typeof setTimeout>
  const guarded = new Promise<T>((resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Request "${key}" timed out`)),
      SINGLE_FLIGHT_TIMEOUT_MS,
    )
    fn().then(resolve, reject)
  }).finally(() => {
    clearTimeout(timer)
    _inFlight.delete(key)
  })
  _inFlight.set(key, guarded)
  return guarded
}

// On a 409 the server's orchestration state has moved on (or the client was out
// of sync). Re-sync so OnboardingV2Page re-routes to the correct screen (or
// redirects to /library when the session is already complete) instead of wedging
// the user on a stale screen. Non-409 errors propagate unchanged.
async function reconcile409<T>(
  fetchState: () => Promise<void>,
  action: () => Promise<T>,
): Promise<T> {
  try {
    return await action()
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 409) {
      await fetchState()
    }
    throw err
  }
}

export const useOnboardingV2Store = create<OnboardingV2Store>((set, get) => ({
  state: null,
  sources: [],
  review: null,
  loadState: 'idle',
  error: null,

  fetchState: async () => {
    set({ loadState: 'loading', error: null })
    try {
      const res = await api.get<ApiEnvelope<OnboardingV2StatePayload>>(`${BASE_PATH}/state`)
      set({
        state: res.data,
        sources: res.data.sources ?? [],
        loadState: 'ready',
      })
    } catch (err) {
      set({ error: messageOf(err), loadState: 'error' })
    }
  },

  skip: async () => {
    set({ error: null })
    await api.post(`${BASE_PATH}/skip`)
    await get().fetchState()
  },

  stageFile: async (file: File) => {
    // Keyed by file identity so a double-fired drop dedupes, but two distinct
    // files staged back-to-back are not collapsed into one.
    await singleFlight(`stageFile:${file.name}:${file.size}:${file.lastModified}`, async () => {
      const form = new FormData()
      form.append('file', file)
      const res = await api.postForm<ApiEnvelope<{ source: SourceSummary; sources_total: number }>>(
        `${BASE_PATH}/sources/file`,
        form,
      )
      set((s) => ({ sources: [...s.sources, res.data.source] }))
    })
  },

  stageText: async (text: string) => {
    await singleFlight(`stageText:${text}`, async () => {
      const res = await api.post<ApiEnvelope<{ source: SourceSummary; sources_total: number }>>(
        `${BASE_PATH}/sources/text`,
        { text },
      )
      set((s) => ({ sources: [...s.sources, res.data.source] }))
    })
  },

  removeSource: async (sourceId: string) => {
    await api.delete(`${BASE_PATH}/sources/${sourceId}`)
    set((s) => ({ sources: s.sources.filter((src) => src.source_id !== sourceId) }))
  },

  process: async () => {
    await singleFlight('process', () =>
      reconcile409(get().fetchState, async () => {
        await api.post(`${BASE_PATH}/process`)
        await get().fetchState()
      }),
    )
  },

  saveProfile: async (payload: ProfileSavePayload) => {
    return reconcile409(get().fetchState, async () => {
      const res = await api.post<ApiEnvelope<{ orchestration_state: OrchestrationState }>>(
        `${BASE_PATH}/profile`,
        payload,
      )
      const next = res.data.orchestration_state
      await get().fetchState()
      return next
    })
  },

  continueToReview: async () => {
    await reconcile409(get().fetchState, async () => {
      await api.post(`${BASE_PATH}/continue-to-review`)
      await get().fetchState()
    })
  },

  retryExtraction: async () => {
    await singleFlight('retryExtraction', () =>
      reconcile409(get().fetchState, async () => {
        await api.post(`${BASE_PATH}/retry-extraction`)
        await get().fetchState()
      }),
    )
  },

  fetchNextThinQuestion: async () => {
    const res = await api.get<ApiEnvelope<{ question: ThinQuestion | null }>>(
      `${BASE_PATH}/thin-questions/next`,
    )
    return res.data.question
  },

  answerThinQuestion: async (questionId: string, answer: string) => {
    const res = await api.post<ApiEnvelope<{ next_question: ThinQuestion | null; thin_remaining: number }>>(
      `${BASE_PATH}/thin-questions/${questionId}/answer`,
      { answer },
    )
    return res.data.next_question
  },

  skipThinQuestion: async (questionId: string) => {
    const res = await api.post<ApiEnvelope<{ next_question: ThinQuestion | null; thin_remaining: number }>>(
      `${BASE_PATH}/thin-questions/${questionId}/skip`,
    )
    return res.data.next_question
  },

  fetchReview: async () => {
    set({ error: null })
    try {
      const res = await api.get<ApiEnvelope<ReviewPayload>>(`${BASE_PATH}/review`)
      set({ review: res.data })
    } catch (err) {
      set({ error: messageOf(err) })
      throw err
    }
  },

  // Review per-item actions reconcile on 409: if the session moved on (e.g.
  // committed in another tab), fetchState re-routes — now safe since a
  // completed user resolves to a terminal /library redirect, not a fresh
  // Welcome session (M-B). Errors propagate so the card can surface them (L-C).
  editProof: async (proofId: string, patch: ProofEditPatch) => {
    await reconcile409(get().fetchState, async () => {
      await api.patch(`${BASE_PATH}/proofs/${proofId}`, patch)
      await get().fetchReview()
    })
  },

  discardProof: async (proofId: string) => {
    await reconcile409(get().fetchState, async () => {
      await api.post(`${BASE_PATH}/proofs/${proofId}/discard`)
      await get().fetchReview()
    })
  },

  restoreProof: async (proofId: string) => {
    await reconcile409(get().fetchState, async () => {
      await api.post(`${BASE_PATH}/proofs/${proofId}/restore`)
      await get().fetchReview()
    })
  },

  answerQuestion: async (questionId: string, value: string, customValue?: string) => {
    await reconcile409(get().fetchState, async () => {
      await api.post(`${BASE_PATH}/questions/${questionId}/answer`, {
        value,
        custom_value: customValue ?? null,
      })
      await get().fetchReview()
    })
  },

  skipQuestion: async (questionId: string) => {
    await reconcile409(get().fetchState, async () => {
      await api.post(`${BASE_PATH}/questions/${questionId}/skip`)
      await get().fetchReview()
    })
  },

  // commit is single-flighted (double-click guard) but NOT reconcile409-wrapped:
  // ReviewScreen handles a commit 409 ("already committed") by navigating
  // straight to /library, so we avoid a competing fetchState here.
  commit: async (skipReview = false) => {
    return singleFlight('commit', async () => {
      const query = skipReview ? '?skip_review=true' : ''
      const res = await api.post<ApiEnvelope<CommitResult>>(`${BASE_PATH}/commit${query}`)
      return res.data
    })
  },
}))
